import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import {
  ILeakScoreDateChartData,
  ILeakScoreDateChartDataQueryParams,
} from '@app/api/interfaces/chart-data/date-leak-score';
import {
  IAiPredictorDateChartData,
  IAiPredictorDateChartDataQueryParams,
} from '@app/api/interfaces/chart-data/date-ai-predictor';
import {
  ILowestDecibelDateChartData,
  ILowestDecibelDateChartDataQueryParams,
} from '@app/api/interfaces/chart-data/date-lowest-decibel';
import { ISpreadDateChartData, ISpreadDateChartDataQueryParams } from '@app/api/interfaces/chart-data/date-spread';
import { prepareParamsForRequest } from '@app/core/methods/http-params';
import { LoggerDeploymentDateLeakScoreMockService } from '@mock/data-services/logger-deployment-date-leak-score-mock.service';
import { LoggerDeploymentDateAiPredictorMockService } from '@mock/data-services/logger-deployment-date-ai-predictor-mock.service';
import { LoggerDeploymentDateLowestDecibelMockService } from '@mock/data-services/logger-deployment-date-lowest-decibel-mock.service';
import { LoggerDeploymentDateSpreadMockService } from '@mock/data-services/logger-deployment-date-spread-mock.service';

@Injectable({
  providedIn: 'root',
})
export class LoggerDeploymentChartDataApiService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly MOCK_LEAK_SCORE: LoggerDeploymentDateLeakScoreMockService,
    private readonly MOCK_AI_PREDICTOR: LoggerDeploymentDateAiPredictorMockService,
    private readonly MOCK_LOWEST_DECIBEL: LoggerDeploymentDateLowestDecibelMockService,
    private readonly MOCK_SPREAD: LoggerDeploymentDateSpreadMockService
  ) {}

  public getDateLeakScore(
    projectId: string,
    deploymentId: string,
    filter: ILeakScoreDateChartDataQueryParams
  ): Observable<IApiResponse<ILeakScoreDateChartData, object>> {
    if (this.MOCK_LEAK_SCORE.isMockEnabled()) return this.MOCK_LEAK_SCORE.get(projectId, deploymentId, filter);

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/chart-data/date-leak-score`;

    return this.httpClient.get<IApiResponse<ILeakScoreDateChartData, object>>(url, options);
  }

  public getDateAiPredictor(
    projectId: string,
    deploymentId: string,
    filter: IAiPredictorDateChartDataQueryParams
  ): Observable<IApiResponse<IAiPredictorDateChartData, object>> {
    if (this.MOCK_AI_PREDICTOR.isMockEnabled()) return this.MOCK_AI_PREDICTOR.get(projectId, deploymentId, filter);

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/chart-data/date-leak-score-ml`;

    return this.httpClient.get<IApiResponse<IAiPredictorDateChartData, object>>(url, options);
  }

  public getDateLowestDecibel(
    projectId: string,
    deploymentId: string,
    filter: ILowestDecibelDateChartDataQueryParams
  ): Observable<IApiResponse<ILowestDecibelDateChartData, object>> {
    if (this.MOCK_LOWEST_DECIBEL.isMockEnabled()) return this.MOCK_LOWEST_DECIBEL.get(projectId, deploymentId, filter);

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/chart-data/date-lowest-decibel`;

    return this.httpClient.get<IApiResponse<ILowestDecibelDateChartData, object>>(url, options);
  }

  public getDateSpread(
    projectId: string,
    deploymentId: string,
    filter: ISpreadDateChartDataQueryParams
  ): Observable<IApiResponse<ISpreadDateChartData, object>> {
    if (this.MOCK_SPREAD.isMockEnabled()) return this.MOCK_SPREAD.get(projectId, deploymentId, filter);

    const filterForParams = prepareParamsForRequest(filter);

    const params = new HttpParams({
      fromObject: {
        ...(filterForParams || {}),
      },
    });

    const options = {
      params,
      withCredentials: true,
    };

    const url = `${environment.apiBaseUrl}/v1/projects/${projectId}/logger-deployments-data/${deploymentId}/chart-data/date-spread`;

    return this.httpClient.get<IApiResponse<ISpreadDateChartData, object>>(url, options);
  }
}
